import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Divider, units } from '@hbf/dsl';

import { UserEntity } from 'ha/types/UserEntity';
import {
  UserCardProfile,
  UserCardVerification,
  UserCardStats,
  UserCardReportBtn,
  UserCardResponseDetails,
  UserCardAge,
  UserCardGender,
  UserCardUniversity,
  UserCardContactDetails,
  UserCardSharedDocuments,
  UserCard,
} from 'ha/components.legacy/UserCard';
import { UserCardOccupation } from 'ha/components.legacy/UserCard/UserCardOccupation';

const useStyles = makeStyles()(() => ({
  divider: {
    marginTop: units.rem(1),
  },
}));

function shouldShowVerifications(user: UserEntity) {
  return (
    user.isEmailVerified ||
    user.hasPhone ||
    user.hasFacebookProfile ||
    user.hasPaymentMethod
  );
}

function shouldShowStats(user: UserEntity) {
  return user.completedBookingsCount > 0 && user.listingsCount > 0;
}

interface Props {
  user: UserEntity;
  hideUserAvatarSection?: boolean;
  hideVerifications?: boolean;
  showDescription?: boolean;
  showResponseDetails?: boolean;
  showReportBtn?: boolean;
  showAge?: boolean;
  showGender?: boolean;
  showOccupation?: boolean;
  showUniversity?: boolean;
  showContactDetails?: boolean;
  showSharedDocuments?: boolean;
  sharedDocumentsCount?: number;
  onShowSharedDocuments?: () => void;
}

const UserCardDefault: React.FC<Props> = ({
  user,
  children,
  showResponseDetails,
  showDescription = true,
  showUniversity = false,
  showReportBtn,
  showAge,
  showGender,
  showOccupation,
  showSharedDocuments = false,
  onShowSharedDocuments = () => {},
  sharedDocumentsCount = 0,
  hideUserAvatarSection,
  hideVerifications,
  ...rest
}) => {
  const { classes } = useStyles();

  const showVerifications = !hideVerifications && shouldShowVerifications(user);
  const showStats = shouldShowStats(user);
  const showContactDetails =
    rest.showContactDetails && (user.email || user.phone);

  return (
    <UserCard data-test-locator="UserCard - Default">
      {!hideUserAvatarSection ? (
        <UserCardProfile
          user={user}
          linkToProfile
          showDescription={showDescription}
          {...rest}
        />
      ) : null}

      {children}

      {showVerifications ? <UserCardVerification user={user} /> : null}

      {showStats && !showVerifications ? <Divider kind="spaced-m" /> : null}

      {showUniversity && <UserCardUniversity university={user.study} />}

      {showResponseDetails ? <UserCardResponseDetails user={user} /> : null}

      {showResponseDetails && showStats ? <Divider kind="spaced-m" /> : null}

      {showGender && <UserCardGender gender={user.gender} />}

      {showAge && <UserCardAge birthDate={user.birthDate} />}

      {showOccupation && <UserCardOccupation occupation={user.occupation} />}

      {showStats ? <UserCardStats user={user} /> : null}

      {showContactDetails && (
        <React.Fragment>
          <Divider kind="spaced-m" />
          <UserCardContactDetails user={user} />
          {!showReportBtn && <Divider />}
        </React.Fragment>
      )}
      {showSharedDocuments && (
        <React.Fragment>
          <Divider className={classes.divider} />
          <UserCardSharedDocuments
            hasMargins
            sharedDocumentsCount={sharedDocumentsCount}
            name={user.firstName}
            onShowSharedDocuments={onShowSharedDocuments}
          />
        </React.Fragment>
      )}
      {showReportBtn ? (
        <React.Fragment>
          <Divider className={classes.divider} />
          <UserCardReportBtn reportedUserID={user.entityId} />
        </React.Fragment>
      ) : null}
    </UserCard>
  );
};

export { UserCardDefault };
